import React from "react";
import RaindropSlider from "../../../ui-components/raindrop-slider/raindrop-slider.tsx";

import { ordinal } from "../../../logic/logic.js";
import { useState } from "react";
import { motion } from "framer-motion";
import RaindropButton, {
  ButtonStyle,
} from "../../../ui-components/raindrop-button/raindrop-button.tsx";

import StageTracker from "../../../ui-components/stage-tracker/stage-tracker";

import RaindropScrollContainer from "../../../ui-components/raindrop-scroll-container/raindrop-scroll-container.tsx";

import MembershipCard from "../../../ui-components/membership-card/membership-card";
import { fetchAccountPlanPost } from "../../../services/api-raindrop";
import { useNavigate } from "react-router-dom";

import Currency from "../../../ui-components/currency/currency.tsx";
import { MAX_NUMBER_OF_ORGANISATIONS_EMERALD } from "../../../services/constants.js";

import ContentSelectPaymentAmount from "../../../ui-components/content-select-payment-amount/content-select-payment-amount.tsx";
import { Size } from "../../../logic/enums.ts";

const KindnessOption = ({
  hasSlider = false,
  kindnessOption,
  newKindnessAmount,
  title = "Let's Do Some Good",
  description = "Lets spread some kindness.",
  kindnessAmount = null,
  setNewKindnessAmount = () => {},
  isClickable = true,
}) => {
  const initialKindnessValue = kindnessAmount || kindnessOption.initialSubunits;
  const [value, setValue] = useState(initialKindnessValue);

  let borderColor = "border-color-dark-border-tertiary";
  let hover = "md:hover:scale-95";

  const isSelected = newKindnessAmount === value;

  if (isSelected) {
    borderColor = "border-color-raindrop-primary";
  }

  if (!isClickable) {
    hover = "";
  }

  const onClick = () => {
    setNewKindnessAmount(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, translateY: 20 }}
      animate={{
        opacity: 1,
        translateY: 0,
        transition: {
          duration: 1,
          type: "spring",
          bounce: 0.5,
        },
      }}
      className="w-full max-w-md "
      onClick={onClick}
    >
      <div
        id={"kindness-option"}
        className={`flex flex-col justify-center p-4 rounded-md text-sm items-start gap-2 w-full  border transition-all  ${borderColor} ${hover} 
        bg-color-dark-background-secondary

        `}
      >
        <div className="flex flex-col">
          <Currency
            subunits={value}
            currency="GBP"
            isMonthly={true}
            isStylised={true}
            isShowingDecimals={false}
          />
        </div>

        <div className="flex flex-col gap-1 items-start">
          <div className={"font-bold"}>{title}</div>
          <div className={"text-start text-xs text-color-dark-text-secondary"}>
            {description}
          </div>
        </div>

        {hasSlider && (
          <RaindropSlider
            value={value}
            min={10000}
            max={1000000}
            step={100.0}
            onChange={(value) => setValue(value)}
          />
        )}
      </div>
    </motion.div>
  );
};

const KindnessTitle = ({ text }) => {
  return <div className="text-2xl font-bold ">{text}</div>;
};

const KindnessSubtitle = ({ text }) => {
  return (
    <div className="flex flex-col gap-2 text-color-raindrop-minor-again pb-4">
      <div className="text-sm">{text}</div>
    </div>
  );
};

const ConnectorLine = () => {
  return <div className="h-5 border-r border-color-dark-border-primary" />;
};

const StageIntro = ({ setStage, contributionDay }) => {
  return (
    <div className="flex flex-col w-full h-full gap-4 items-center justify-center overflow-y-scroll">
      <div className="flex flex-col gap-10 items-center justify-center h-full">
        <i className="fas fa-droplet text-8xl text-color-raindrop-primary"></i>
        <h1 className="font-bold text-4xl text-center">
          Let's set up your contribution
        </h1>

        <div className="flex flex-col gap-6 text-center text-color-dark-text-secondary px-4 text-lg">
          <p>
            You have plenty of time to look around and choose your charities.
            We'll start collecting your contribution on the{" "}
            <b className="text-color-raindrop-primary">
              {ordinal(contributionDay)} of each month
            </b>
            .
          </p>
          <p>
            Remember you can change your contribution at any time, even pausing
            entirely.
          </p>
        </div>
      </div>
      <div className="flex flex-row gap-4 h-fit w-full items-center justify-center">
        <RaindropButton
          title={"Set up later"}
          icon={"fa-chevron-left"}
          to={"/raindrop"}
          size={Size.Large}
          isFluid={true}
        />

        <RaindropButton
          title={"Continue"}
          onClick={() => setStage("STAGE_SELECT_PAYMENT_AMOUNT")}
          style={ButtonStyle.Primary}
          size={Size.Large}
          isFluid={true}
        />
      </div>
    </div>
  );
};

const StageSelectPaymentAmount = ({
  newKindnessAmount,
  setNewKindnessAmount,
  setStage,
}) => {
  let title = "How much would you like to give?";

  const onBack = () => {
    setStage("STAGE_INTRO");
  };

  const onConfirm = () => {
    setStage(
      newKindnessAmount === 0 ? "STAGE_CONFIRM" : "STAGE_SELECT_VERSION"
    );
  };

  return (
    <div className="flex flex-col w-full h-full gap-4">
      <KindnessTitle text={title} />
      <div className="flex flex-col overflow-y-scroll h-full gap-2 items-center">
        <KindnessSubtitle
          text={
            "This is your one and only monthly payment. You'll choose how to distribute it soon"
          }
        />

        <ContentSelectPaymentAmount
          newKindnessAmount={newKindnessAmount}
          setNewKindnessAmount={setNewKindnessAmount}
        />
      </div>

      <div
        className={
          "flex flex-row gap-2 h-fit w-full items-center justify-center "
        }
      >
        <RaindropButton
          icon={"fa-chevron-left"}
          title={"Back"}
          onClick={onBack}
          style={ButtonStyle.Secondary}
          size={Size.Large}
          isFluid={true}
        />
        <RaindropButton
          icon={"fa-check"}
          title={"Next"}
          onClick={onConfirm}
          isDisabled={newKindnessAmount === null}
          style={ButtonStyle.Primary}
          size={Size.Large}
          isFluid={true}
        />
      </div>
    </div>
  );
};

const StageSelectVersion = ({
  newIsPremiumMember,
  setNewIsPremiumMember,
  setCurrentModalComponent,
  setStage,
  values,
}) => {
  const onBack = () => {
    setStage("STAGE_SELECT_PAYMENT_AMOUNT");
  };

  const onConfirm = () => {
    if (
      values.length > MAX_NUMBER_OF_ORGANISATIONS_EMERALD &&
      newIsPremiumMember === false
    ) {
      console.log(
        "Cannot move to confirm stage if you have more than three charities. (RD-3242)"
      );
      setCurrentModalComponent(
        <div>
          Cannot move to the emerald version while supporting more than three
          organisations
        </div>
      );
      return;
    }
    setStage("STAGE_CONFIRM");
  };

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <KindnessTitle text={"Which version would you like?"} />
      <RaindropScrollContainer>
        <KindnessSubtitle
          text={
            "We want to make Raindrop as accessible as possible, so choose the version you feel most comfortable with."
          }
        />

        <motion.div
          layout
          className="flex flex-col gap-4 w-full items-center justify-center"
        >
          <MembershipCard
            isRaindropPlusship={false}
            onClick={() => setNewIsPremiumMember(false)}
            selected={newIsPremiumMember === false}
          />

          <MembershipCard
            isRaindropPlusship={true}
            onClick={() => setNewIsPremiumMember(true)}
            selected={newIsPremiumMember === true}
          />
        </motion.div>
      </RaindropScrollContainer>

      <div
        className={
          "flex flex-row gap-2 h-fit w-full items-center justify-center "
        }
      >
        <RaindropButton
          icon={"fa-chevron-left"}
          title={"Back"}
          onClick={onBack}
          style={ButtonStyle.Secondary}
          size={Size.Large}
          isFluid={true}
        />
        <RaindropButton
          icon={"fa-check"}
          title={"Next"}
          onClick={onConfirm}
          size={Size.Large}
          isFluid={true}
          isDisabled={newIsPremiumMember === null}
          style={ButtonStyle.Primary}
        />
      </div>
    </div>
  );
};

const StageQuestionaire = ({ setStage }) => {
  const onBack = () => {
    setStage("STAGE_SELECT_PAYMENT_AMOUNT");
  };

  const onConfirm = () => {
    setStage("STAGE_CONFIRM");
  };

  return (
    <>
      {/* <KindnessTitle text={"Life can be tough, we understand"} /> */}

      <motion.div
        initial={{ opacity: 0, translateY: 20 }}
        animate={{
          opacity: 1,
          translateY: 0,
        }}
        transition={{ duration: 1, type: "spring", bounce: 0.5 }}
        className="flex flex-col w-full h-full gap-4 items-center justify-center"
      >
        <i className="fas fa-heart text-4xl text-color-raindrop-primary"></i>
        <h1 className="text-2xl font-bold">Life can be tough, we understand</h1>
        <p className="text-color-dark-text-secondary">
          We'll be here if you want to resume
        </p>
      </motion.div>

      <div
        className={
          "flex flex-row gap-8 h-fit w-full items-center justify-center "
        }
      >
        <RaindropButton
          icon={"fa-chevron-left"}
          title={"Back"}
          onClick={onBack}
          style={ButtonStyle.Secondary}
          size={Size.Large}
          isFluid={true}
        />
        <RaindropButton
          icon={"fa-check"}
          title={"Next"}
          onClick={onConfirm}
          style={ButtonStyle.Primary}
          size={Size.Large}
          isFluid={true}
        />
      </div>
    </>
  );
};

const StageConfirm = ({
  newKindnessAmount,
  newIsPremiumMember,
  onSubmit,
  setStage,
}) => {
  const showMembershipCardAtEnd = newKindnessAmount !== 0;

  const onBack = () => {
    let backStage = "STAGE_SELECT_VERSION";
    if (newKindnessAmount === 0) {
      backStage = "STAGE_QUESTIONAIRE";
    }
    setStage(backStage);
  };

  let title = "Confirm this is correct?";

  if (newKindnessAmount === 0) {
    title = "";
  }

  return (
    <div className={"flex flex-col w-full h-full"}>
      <KindnessTitle text={title} />

      {newKindnessAmount > 0 && (
        <RaindropScrollContainer>
          <KindnessSubtitle
            text={
              "Contributions include card and foreign exchange fees. You can change this at any time."
            }
          />
          <div className="flex flex-col items-center h-full justify-center w-full">
            <KindnessOption
              kindnessAmount={newKindnessAmount}
              isClickable={false}
            />
            {showMembershipCardAtEnd && <ConnectorLine />}
            {showMembershipCardAtEnd && (
              <MembershipCard
                isRaindropPlusship={newIsPremiumMember}
                isClickable={false}
              />
            )}
          </div>
        </RaindropScrollContainer>
      )}

      {newKindnessAmount === 0 && (
        <motion.div
          initial={{ opacity: 0, translateY: 20 }}
          animate={{
            opacity: 1,
            translateY: 0,
          }}
          transition={{ duration: 1, type: "spring", bounce: 0.5 }}
          className="flex flex-col w-full h-full gap-4 items-center justify-center"
        >
          <i className="fas fa-heart text-4xl text-color-raindrop-primary"></i>
          <h1 className="text-2xl font-bold">
            Life can be tough, we understand
          </h1>
          <p className="text-color-dark-text-secondary">
            We'll be here if you want to resume
          </p>
        </motion.div>
      )}
      <div
        className={
          "flex flex-row gap-8 h-fit w-full items-center justify-center "
        }
      >
        <RaindropButton
          icon={"fa-chevron-left"}
          title={"Back"}
          onClick={onBack}
          style={ButtonStyle.Secondary}
          size={Size.Large}
          isFluid={true}
        />
        <RaindropButton
          icon={"fa-check"}
          title={"Confirm"}
          onClick={onSubmit}
          style={ButtonStyle.Primary}
          size={Size.Large}
          isFluid={true}
        />
      </div>
    </div>
  );
};

const RaindropKindness = ({
  values,
  setCurrentKindnessAmount,
  setCurrentModalComponent,
  userCurrent,
  setUserCurrent,
}) => {
  const navigate = useNavigate();

  const [newKindnessAmount, setNewKindnessAmount] = useState(null);

  const [newIsPremiumMember, setNewIsPremiumMember] = useState(null);

  const contributionDay =
    userCurrent?.account?.accountPlan?.contributionDay ||
    ((new Date().getDate() + 5) % 28) + 1;
  console.warn(
    "(RD-3203) - contributionDay we shouldn't be computing here",
    contributionDay
  );

  const STAGE_CHOICES = [
    "STAGE_INTRO",
    "STAGE_SELECT_PAYMENT_AMOUNT",
    "STAGE_SELECT_VERSION",
    "STAGE_QUESTIONAIRE",
    "STAGE_CONFIRM",
  ];
  const [stage, setStage] = useState("STAGE_INTRO");

  const setIsRaindropPlus = (isPlus) => {
    setUserCurrent({
      ...userCurrent,
      account: {
        ...userCurrent.account,
        isRaindropPlus: isPlus,
      },
    });
  };

  const onSubmit = () => {
    fetchAccountPlanPost({
      accountId: userCurrent.account.id,
      subunits: newKindnessAmount,
      isSupportingRaindrop: newIsPremiumMember,
      contributionDay: contributionDay,
    })
      .then((data) => {
        userCurrent.status = "USER_STATUS_ACTIVE";
        setIsRaindropPlus(newIsPremiumMember);
        setCurrentKindnessAmount(newKindnessAmount);
        if (!userCurrent.account.paymentMethodActive) {
          navigate("/raindrop/payment-method-add");
          return;
        }
        navigate("/raindrop");
      })
      .catch((error) => {
        alert(`🔴 (RD-320) - There was an error, please try again. ${error}`);
      });
  };

  return (
    <div className="flex flex-col h-full w-full justify-center items-center px-4">
      <div
        id="page-kindness"
        className={
          "flex flex-col h-full w-full justify-center items-center gap-4 py-8 max-w-2xl"
        }
      >
        {/* <JSONBlob data={userCurrent} /> */}
        <StageTracker
          numberOfStages={STAGE_CHOICES.length}
          stageIndex={STAGE_CHOICES.indexOf(stage)}
        />

        {stage === "STAGE_INTRO" && (
          <StageIntro
            userCurrent={userCurrent}
            setStage={setStage}
            contributionDay={contributionDay}
          />
        )}
        {stage === "STAGE_SELECT_PAYMENT_AMOUNT" && (
          <StageSelectPaymentAmount
            newKindnessAmount={newKindnessAmount}
            setNewKindnessAmount={setNewKindnessAmount}
            setStage={setStage}
          />
        )}
        {stage === "STAGE_QUESTIONAIRE" && (
          <StageQuestionaire setStage={setStage} />
        )}
        {stage === "STAGE_SELECT_VERSION" && (
          <StageSelectVersion
            values={values}
            setCurrentModalComponent={setCurrentModalComponent}
            newIsPremiumMember={newIsPremiumMember}
            setNewIsPremiumMember={setNewIsPremiumMember}
            setStage={setStage}
          />
        )}
        {stage === "STAGE_CONFIRM" && (
          <StageConfirm
            onSubmit={onSubmit}
            setStage={setStage}
            newIsPremiumMember={newIsPremiumMember}
            newKindnessAmount={newKindnessAmount}
          />
        )}
      </div>
    </div>
  );
};

export default RaindropKindness;
